import { supabase } from "../supabaseClient";
import {
  emptySquadLaneState,
  emptyTournamentLaneSettings,
  formatPair,
  laneStateFromSquadAndTeams,
  movementRulesFromUi,
  pairStartFromLane,
  planStartingLaneSave,
  settingsFromTournamentRow,
} from "./laneAssignment";

export const isLaneSlotTakenError = (error) =>
  error?.code === "23505" ||
  /starting_pair_slot|duplicate key/i.test(error?.message || "");

const actorId = async () => {
  const { data } = await supabase.auth.getUser();
  return data?.user?.id || null;
};

export const fetchTournamentLaneSettings = async (tournamentId) => {
  if (!tournamentId) return emptyTournamentLaneSettings();
  const { data, error } = await supabase
    .from("tournamentLaneSettings")
    .select("movementRules, encoding, doublesPlacement")
    .eq("tournamentID", tournamentId)
    .maybeSingle();
  if (error) throw error;
  return settingsFromTournamentRow(data);
};

export const saveTournamentLaneSettings = async (tournamentId, settings) => {
  if (!tournamentId) {
    throw new Error("Tournament is required.");
  }
  const userId = await actorId();
  const movementRules = settings?.movementRules?.kind
    ? settings.movementRules
    : movementRulesFromUi(settings);
  const now = new Date().toISOString();
  const row = {
    tournamentID: tournamentId,
    movementRules,
    encoding: settings?.encoding || "names",
    doublesPlacement: settings?.doublesPlacement || "split",
    updatedAt: now,
    updatedBy: userId,
  };

  const { data: existing, error: existingError } = await supabase
    .from("tournamentLaneSettings")
    .select("tournamentID, createdBy")
    .eq("tournamentID", tournamentId)
    .maybeSingle();
  if (existingError) throw existingError;

  const { error } = existing
    ? await supabase
        .from("tournamentLaneSettings")
        .update(row)
        .eq("tournamentID", tournamentId)
    : await supabase.from("tournamentLaneSettings").insert({
        ...row,
        createdAt: now,
        createdBy: userId,
      });
  if (error) throw error;
  return settingsFromTournamentRow(row);
};

export const saveSquadLaneRange = async (
  squadId,
  { startLane, endLane, blackouts }
) => {
  if (!squadId) {
    throw new Error("Squad is required.");
  }
  const { error } = await supabase
    .from("squads")
    .update({
      startLane: pairStartFromLane(startLane),
      endLane: pairStartFromLane(endLane),
      blackouts: (blackouts || [])
        .map((value) => pairStartFromLane(value))
        .filter(Boolean),
    })
    .eq("id", squadId);
  if (error) throw error;
};

export const fetchSquadLaneStates = async (squadIds) => {
  const ids = [...new Set((squadIds || []).filter(Boolean))];
  if (!ids.length) return {};

  const [squadResult, teamResult] = await Promise.all([
    supabase
      .from("squads")
      .select("id, startLane, endLane, blackouts")
      .in("id", ids),
    supabase
      .from("teams")
      .select("id, squadID, startingPair, startingLane, slot")
      .in("squadID", ids)
      .eq("isDeleted", false),
  ]);
  if (squadResult.error) throw squadResult.error;
  if (teamResult.error) throw teamResult.error;

  const squadById = Object.fromEntries(
    (squadResult.data || []).map((row) => [row.id, row])
  );
  const teamsBySquad = {};
  (teamResult.data || []).forEach((team) => {
    if (!team?.squadID) return;
    (teamsBySquad[team.squadID] ||= []).push(team);
  });

  const next = {};
  ids.forEach((id) => {
    next[id] = laneStateFromSquadAndTeams(
      squadById[id] || { id },
      teamsBySquad[id] || []
    );
  });
  return next;
};

export const fetchSquadLaneState = async (squadId) => {
  if (!squadId) return emptySquadLaneState();
  const bySquad = await fetchSquadLaneStates([squadId]);
  return bySquad[squadId] || emptySquadLaneState();
};

const placementEntries = (placements) =>
  Object.entries(placements || {}).filter(
    ([, value]) => pairStartFromLane(value?.pairStart) && Number(value?.slot) >= 1
  );

const startingLaneForPair = (pairStart, startingLane) => {
  const start = pairStartFromLane(pairStart);
  const lane = Number(startingLane);
  if (!start) return null;
  if (lane === start || lane === start + 1) return lane;
  return null;
};

export const saveSquadLaneAssignments = async ({
  squadId,
  teamIds,
  teams,
  placements,
}) => {
  if (!squadId) {
    throw new Error("Squad is required.");
  }
  const ids = (teamIds || teams || [])
    .map((item) => (typeof item === "string" ? item : item?.id))
    .filter(Boolean);
  if (!ids.length) return;

  const assigned = placementEntries(placements).filter(([teamId]) =>
    ids.includes(teamId)
  );
  const eventByTeam = Object.fromEntries(
    (teams || [])
      .filter((team) => team?.id)
      .map((team) => [team.id, team.eventsID || ""])
  );
  const seen = new Set();
  assigned.forEach(([teamId, value]) => {
    const key = `${eventByTeam[teamId] || ""}:${pairStartFromLane(
      value.pairStart
    )}:${Number(value.slot)}`;
    if (seen.has(key)) {
      const error = new Error("That pair already has a team in that place.");
      error.code = "23505";
      throw error;
    }
    seen.add(key);
  });

  const { error: clearError } = await supabase
    .from("teams")
    .update({ startingPair: null, startingLane: null, slot: null })
    .eq("squadID", squadId)
    .eq("isDeleted", false)
    .in("id", ids);
  if (clearError) throw clearError;

  for (const [teamId, value] of assigned) {
    const pairStart = pairStartFromLane(value.pairStart);
    const { error } = await supabase
      .from("teams")
      .update({
        startingPair: pairStart,
        startingLane: startingLaneForPair(pairStart, value.startingLane),
        slot: Number(value.slot),
      })
      .eq("id", teamId)
      .eq("squadID", squadId)
      .eq("isDeleted", false);
    if (error) throw error;
  }
};

export const assignTeamStartingPair = async ({
  teamId,
  startingPair,
  startingLane = null,
  slot,
}) => {
  if (!teamId) return;
  const pairStart = pairStartFromLane(startingPair);
  const slotValue = Number(slot);
  if (!pairStart || slotValue < 1) return;
  const { error } = await supabase
    .from("teams")
    .update({
      startingPair: pairStart,
      startingLane: startingLaneForPair(pairStart, startingLane),
      slot: slotValue,
    })
    .eq("id", teamId)
    .eq("isDeleted", false);
  if (error) throw error;
};

export const clearTeamStartingPair = async (teamId) => {
  if (!teamId) return;
  const { error } = await supabase
    .from("teams")
    .update({ startingPair: null, startingLane: null, slot: null })
    .eq("id", teamId)
    .eq("isDeleted", false);
  if (error) throw error;
};

export const saveEditTeamStartingLane = async ({
  team,
  laneInput,
  tournamentId,
}) => {
  if (!team?.id) {
    throw new Error("Team is required.");
  }

  let squadRow = null;
  let squadTeams = [];
  if (team.squadID) {
    const [squadResult, teamResult] = await Promise.all([
      supabase
        .from("squads")
        .select("id, startLane, endLane, blackouts")
        .eq("id", team.squadID)
        .maybeSingle(),
      supabase
        .from("teams")
        .select("id, squadID, eventsID, startingPair, startingLane, slot")
        .eq("squadID", team.squadID)
        .eq("isDeleted", false),
    ]);
    if (squadResult.error) throw squadResult.error;
    if (teamResult.error) throw teamResult.error;
    squadRow = squadResult.data;
    squadTeams = teamResult.data || [];
  }

  let bowlerQuantity = 1;
  if (team.eventsID) {
    const { data, error } = await supabase
      .from("tournamentEvents")
      .select("bowlerQuantity")
      .eq("id", team.eventsID)
      .maybeSingle();
    if (error) throw error;
    bowlerQuantity = data?.bowlerQuantity || 1;
  }

  const settings = await fetchTournamentLaneSettings(tournamentId);
  const plan = planStartingLaneSave({
    laneInput,
    team,
    squadTeams,
    squadRow,
    settings,
    bowlerQuantity,
  });
  if (plan.error) {
    const error = new Error(plan.error);
    error.code = "PAIR_FULL";
    throw error;
  }

  if (!plan.startingPair) {
    await clearTeamStartingPair(team.id);
    return plan;
  }

  try {
    await assignTeamStartingPair({
      teamId: team.id,
      startingPair: plan.startingPair,
      startingLane: plan.startingLane,
      slot: plan.slot,
    });
  } catch (error) {
    if (isLaneSlotTakenError(error)) {
      const taken = new Error(`Pair ${formatPair(plan.startingPair)} is full.`);
      taken.code = "PAIR_FULL";
      throw taken;
    }
    throw error;
  }
  return plan;
};

export const attachLaneFields = async (payload) => {
  if (!payload) return payload;
  const squads = payload.squads || [];
  const teams = payload.teams || [];
  const needsSquad = squads.some((squad) => squad.startLane === undefined);
  const needsTeam = teams.some(
    (team) =>
      team.startingPair === undefined || team.startingLane === undefined
  );
  if (!needsSquad && !needsTeam) return payload;

  const [squadResult, teamResult] = await Promise.all([
    needsSquad && squads.length
      ? supabase
          .from("squads")
          .select("id, startLane, endLane, blackouts, movementRules")
          .in(
            "id",
            squads.map((squad) => squad.id)
          )
      : Promise.resolve({ data: null, error: null }),
    needsTeam && teams.length
      ? supabase
          .from("teams")
          .select("id, startingPair, startingLane, slot")
          .in(
            "id",
            teams.map((team) => team.id)
          )
      : Promise.resolve({ data: null, error: null }),
  ]);
  if (squadResult.error) throw squadResult.error;
  if (teamResult.error) throw teamResult.error;

  const squadById = Object.fromEntries(
    (squadResult.data || []).map((row) => [row.id, row])
  );
  const teamById = Object.fromEntries(
    (teamResult.data || []).map((row) => [row.id, row])
  );
  return {
    ...payload,
    squads: squads.map((squad) => ({
      ...squad,
      ...(squadById[squad.id] || {}),
    })),
    teams: teams.map((team) => ({
      ...team,
      ...(teamById[team.id] || {}),
    })),
  };
};
